import React from 'react';
import styled from 'styled-components';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { AnimatedSection } from '../../components/common/AnimatedSection';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
  padding-top: 10%;
  @media (max-width: 768px) {
    padding-top: 30%;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 3rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;
  padding: 3rem 0;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111111;
  margin-bottom: 1.5rem;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 2rem;
    
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.1rem;
  color: #6b7280;
  font-weight: 400;
  white-space: normal; 
  @media (max-width: 768px) {
    font-size: 0.9rem;
    white-space: pre-line;
  }
`;

const TimelineWrapper = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 0 4rem 0;
  display: grid;
  grid-auto-rows: minmax(300px, auto); /* 최소 300px 높이 보장 */
  row-gap: 4rem; /* 연도 간 고정 간격 */

  /* 중앙 세로선 */
  &::before {
  content: '';
  position: absolute;
  left: 50%;
  top: -20px;                       /* 화살표 높이만큼 내려서 시작 */
  bottom: 0;
  width: 3px;
  background: 
    // linear-gradient(to top, #3b82f6 0%, #60a5fa 30%, #60a5fa 60%, transparent 100%);
    repeating-linear-gradient(
      to top,
      transparent 0 10px,
rgba(167, 204, 250, 0.79) 10px 13px
    );                              /* 실선 + 점선 섞기 */
  background-size: 100% 100%, 100% 100%;
  background-repeat: no-repeat;
  transform: translateX(-50%);
  z-index: 0;
}

/* 화살표 */
&::after {
  content: '';
  position: absolute;
  left: 50%;
  top: -50px;
  transform: translateX(-50%);
  border: 10px solid transparent;
  border-bottom: 14px solid rgba(167, 204, 250, 0.79);  /* 화살표 삼각형 */
}
`;

const TimelineItem = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr; /* 두 열 그리드 */
  align-items: start;

  /* 왼쪽 배치 (홀수 항목) */
  &.left {
    grid-template-areas: "year content";
    
    .year-marker {
      grid-area: year;
      justify-self: end;
      margin-right: -12px;
    }
    
    .year-title {
      grid-area: year;
      justify-self: end;
      text-align: right;
      padding-right: 3rem;
    }
    
    .content-area {
      grid-area: content;
      padding-left: 3rem;
      text-align: left;

      @media (max-width: 768px) {
        padding-left: 2rem;
      }
    }
  }

  /* 오른쪽 배치 (짝수 항목) */
  &.right {
    grid-template-areas: "content year";
    
    .year-marker {
      grid-area: year;
      justify-self: start;
      margin-left: -12px;
    }
    
    .year-title {
      grid-area: year;
      justify-self: start;
      text-align: left;
      padding-left: 3rem;
    }
    
    .content-area {
      grid-area: content;
      padding-right: 3rem;
      text-align: right;
      @media (max-width: 768px) {
        padding-right: 2rem;
      }
    }
  }

  
`;

const YearMarker = styled.div`
  width: 24px;
  height: 24px;
  background: #3b82f6;
  border: 4px solid #ffffff;
  border-radius: 4px;
  z-index: 105;
  box-shadow: 0 0 0 3px #3b82f6;
  align-self: start;
  margin-top: 1rem;
`;

const YearTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #3b82f6;
  margin: 0;
  letter-spacing: -2px;
  align-self: start;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ContentArea = styled.div`
  align-self: start;
  padding-top: 1rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MonthItem = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MonthHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 0.8rem;

  /* 오른쪽 정렬일 때 순서 변경 */
  ${props => props.isRightAlign && `
    flex-direction: row-reverse;
    text-align: right;
  `}

  /* 모바일에선 숫자 위, 활동 아래 (가로→세로) */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;   /* isRightAlign 무시 */
    gap: 0.35rem;       /* 숫자와 활동 사이 간격 */
  }
`;

const MonthNumber = styled.div`
  color: #3b82f6;
  font-weight: 700;
  font-size: 1rem;
  min-width: 3rem;
  flex-shrink: 0;
  @media (max-width: 768px) {
    min-width: 0;       /* 3rem 고정폭 해제 → 텍스트 폭 더 확보 */
    font-size: 0.95rem; /* 선택: 숫자 살짝 축소 */
  }
`;

const ActivitiesList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  @media (max-width: 768px) {
    width: 100%;        /* 세로 쌓일 때 가로폭 꽉 채움 */
  }
`;

const ActivityItem = styled.div`
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
  white-space: pre-line; /* \n 줄바꿈 지원 */

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

// 연혁 데이터
const historyData = [
  {
    year: 2025,
    events: [
      {
        month: '01',
        activities: [
          '기술혁신인증제품 실증지원사업 선정\n-광주시청 소방안전본부, 종합건설본부, 상수도사업본부 납품완료'
        ]
      },
      {
        month: '03',
        activities: [
          'KOITA 호남경영인클럽 회장 위촉',
          ' 「코리아 나라장터 엑스포 2025」 전시참가'
        ]
      },
      {
        month: '07',
        activities: [
          '「캐리어형 비상표시장치 SG-905」\n행정안전부 재난안전제품인증 선정',
          '「가드레일용 안전계단 디딤-Safe」\n국토교통부 혁신제품 지정',
          '한국도로공사 2025년 중소기업 ESG지원사업 선정'
        ]
      },
    ]
  },
  {
    year: 2024,
    events: [
      {
        month: '01',
        activities: [
          '스마트안전경고시스템 경찰청 교통안전계 납품완료'
        ]
      },
      {
        month: '03',
        activities: [
          'KOITA 호남경영인클럽 수석부회장 위촉',
        ]
      },
      {
        month: '07',
        activities: [
          '성과공유기업 인증',
          '‘가드레일의 편리한 승월을 위한 안전계단’ \n중소기업기술마켓 제품 지정'
        ]
      },
      {
        month: '11',
        activities: [
          '‘안전계단’ 벤처창업혁신조달상품 선정',
          'ISO45001 인증'
        ]
      },
      {
        month: '12',
        activities: [
          ' ‘가드레일용 안전계단 디딤-Safe’ \n한국도로공사 혁신제품 추천서 선정',
          '「2024 중소기업융합대전」 전시참가',
          '국토교통부 혁신제품 추천대상 선정'
        ]
      },
    ]
  },
  {
    year: 2023,
    events: [
      {
        month: '01',
        activities: [
          '벤처기업확인 - 혁신성장유형',
        ]
      },
      {
        month: '07',
        activities: [
          '성과공유기업 인증'
        ]
      },
      {
        month: '08',
        activities: [
          '광주북구사회적경제연합회 우수기술인증기업 선정',
          '광주테크노파크 에너지신산업 OpenLAB 기업 선정'
        ]
      },
      {
        month: '09',
        activities: [
          '프리(PRE)-명품강소기업 선정'
        ]
      },
    ]
  },
  {
    year: 2022,
    events: [
      {
        month: '07',
        activities: [
          '‘캐리어형 비상표시장치’ 재난안전제품 인증',
          '‘반사안전표지판’ KS인증 취득',
          'GIST-NUS Global CEO Leadership programme 수료'
        ]
      },
      {
        month: '08',
        activities: [
          '대한민국모범기업인 대상 수상'
        ]
      },
      {
        month: '10',
        activities: [
          '조달청 나라장터 도로표지 등록',
          '한국도로공사 2022년 교통안전 우수업체 표창'
        ]
      },
      {
        month: '11',
        activities: [
          '‘캐리어형 비상표시장치’ 벤처창업혁신조달상품 선정'
        ]
      },
      {
        month: '12',
        activities: [
          '광주지방 조달청장상 표창',
          '경찰청 스마트 안전경고시스템 230set  납품'
        ]
      },
    ]
  },
  {
    year: 2021,
    events: [
      {
        month: '03',
        activities: [
          '광주시청 안전마을 안전학교 후원 업무협약'
        ]
      },
      {
        month: '08',
        activities: [
          '한국도로공사 중소기업 기술개발지원사업 수행'
        ]
      },
      {
        month: '11',
        activities: [
          'KC인증_비상표시장치, 불꽃신호기 점화장치'
        ]
      },
      {
        month: '12',
        activities: [
          '조달청 나라장터 교통안전표지, 갈매기표지 등록'
        ]
      },
    ]
  },
  {
    year: 2020,
    events: [
      {
        month: '05',
        activities: [
          '경영혁신형 중소기업(Main-Biz) 인가'
        ]
      },
      {
        month: '07',
        activities: [
          '신호표지 직접생산확인증 취득',
          '중소기업기술정보진흥원 디딤돌창업과제 수행'
        ]
      },
      {
        month: '09',
        activities: [
          '대한민국지역사회공헌 대상 수상'
        ]
      },
      {
        month: '12',
        activities: [
          'ISO 9001, 14001 인증 취득'
        ]
      },
    ]
  },
  {
    year: 2019,
    events: [
      {
        month: '04',
        activities: [
          '프랑스 파리 산업박람회 물품참가'
        ]
      },
      {
        month: '11',
        activities: [
          'IR52 장영실 기술혁신상 수상'
        ]
      }
    ]
  },
  {
    year: 2018,
    events: [
      {
        month: '03',
        activities: [
          '일본 도쿄 LED NEXT STAGE 전시회 물품참가',
          '한국발명진흥회 IP나래기업 선정'
        ]
      },
      {
        month: '04',
        activities: [
          '제51회 과학의 날 표창'
        ]
      },
      {
        month: '05',
        activities: [
          '제53회 발명의 날 국무총리상 표창',
          'KOITA 호남기술경영인클럽 사무총장 위임'
        ]
      },
      {
        month: '07',
        activities: [
          '우수발명품 우선구매선정_비상표시장치'
        ]
      },
      {
        month: '10',
        activities: [
          '기술혁신형 중소기업(Inno-Biz) 인가'
        ]
      },
      {
        month: '12',
        activities: [
          '사회적기업 인증'
        ]
      },
    ]
  },
  {
    year: 2017,
    events: [
      {
        month: '03',
        activities: [
          '대한민국 지역사회공헌 대상 수상',
          '광주전남중소기업청 우수중소기업인 표창_'
        ]
      },
      {
        month: '06',
        activities: [
          '중소기업 지식재산 경영인증',
          '대한민국 모범기업인 최고대상 수상',
          '기업부설연구소 지정'
        ]
      },
      {
        month: '07',
        activities: [
          '중소벤처기업부 제품공정개선기술개발사업 선정'
        ]
      },
      {
        month: '09',
        activities: [
          '중소벤처기업부 첫걸음 R&D사업 선정',
          '위험성평가 인정서 취득',
          'CLEAL 사업장 인정서 취득'
        ]
      },
      {
        month: '10',
        activities: [
          '직무발명보상 우수기업 인증'
        ]
      },
    ]
  },
  {
    year: 2016,
    events: [
      {
        month: '03',
        activities: [
          '대한민국 국민브랜드 대상 수상',
          '대한민국 우수상품 대상 수상'
        ]
      },
      {
        month: '12',
        activities: [
          '연구전담부서 설립'
        ]
      }
    ]
  },
  {
    year: 2015,
    events: [
      {
        month: '02',
        activities: [
          '주식회사 세연이앤에스 법인설립',
        ]
      },
      {
        month: '03',
        activities: [
          'IP 스타기업 선정'
        ]
      },
      {
        month: '08',
        activities: [
          '대한민국 모법기업인 대상 수상'
        ]
      },
      {
        month: '12',
        activities: [
          '대한민국 나눔 대상 수상',
          '‘올뺴미’ 국토부 납품 및 설치'
        ]
      },
    ]
  },
  {
    year: 2014,
    events: [
      {
        month: '10',
        activities: [
          '‘올빼미’ 한국도로공사 납품 및 설치'
        ]
      }
    ]
  },
  {
    year: 2013,
    events: [
      {
        month: '02',
        activities: [
          '비탈면 사전감지시스템 ‘올빼미’ 개발'
        ]
      }
    ]
  },
  {
    year: 2012,
    events: [
      {
        month: '10',
        activities: [
          '광주공장 신축'
        ]
      },
      {
        month: '12',
        activities: [
          '제조업 공장등록'
        ]
      },
    ]
  },
  {
    year: 2011,
    events: [
      {
        month: '07',
        activities: [
          '세연이앤에스 설립'
        ]
      }
    ]
  }
];

// 개별 타임라인 아이템 컴포넌트
function TimelineItemComponent({ yearData, index }) {
  const [itemRef, itemVisible] = useScrollAnimation();
  const isLeft = index % 2 === 0;
  
  return (
    <AnimatedSection
      ref={itemRef}
      isVisible={itemVisible}
      animation="fadeInUp"
      delay="0s"
    >
      <TimelineItem className={isLeft ? 'left' : 'right'}>
        <YearMarker className="year-marker" />
        <YearTitle className="year-title">
          {yearData.year}
        </YearTitle>
        <ContentArea className="content-area">
          {yearData.events.map((event, eventIndex) => (
            <MonthItem key={eventIndex}>
              <MonthHeader isRightAlign={!isLeft}>
                <MonthNumber>{event.month}</MonthNumber>
                <ActivitiesList>
                  {event.activities.map((activity, activityIndex) => (
                    <ActivityItem key={activityIndex}>
                      {activity}
                    </ActivityItem>
                  ))}
                </ActivitiesList>
              </MonthHeader>
            </MonthItem>
          ))}
        </ContentArea>
      </TimelineItem>
    </AnimatedSection>
  );
}

// 메인 History 컴포넌트
function History() {
  const [headerRef, headerVisible] = useScrollAnimation();

  return (
    <PageContainer>
      <Container>
        <AnimatedSection 
          ref={headerRef} 
          isVisible={headerVisible} 
          animation="fadeInUp"
        >
          <PageHeader>
            <PageTitle>연혁</PageTitle>
            <PageSubtitle>
              {"설립부터 현재까지, \n세연이앤에스의 성장 여정을 소개합니다."}
            </PageSubtitle>
          </PageHeader>
        </AnimatedSection>

        <TimelineWrapper>
          {historyData.map((yearData, index) => (
            <TimelineItemComponent 
              key={yearData.year} 
              yearData={yearData} 
              index={index} 
            />
          ))}
        </TimelineWrapper>
      </Container>
    </PageContainer>
  );
}

export default History;